import { Card } from '@/components/ui/card'
import { privatize } from '@/lib/utils/revenueUtils'
import { fmtCount, fmtMoney, type SourceRevenueRow } from './attributionView'

/**
 * "Where paying customers came from" (Paper "Attribution · Stripe only, no
 * ads"): payments grouped by the source of the visit they matched. Earned
 * carries a tint bar for its share of everything collected in range.
 */

const COL = 'flex w-[60px] shrink-0 justify-end text-right sm:w-[150px]'

export default function SourcesRevenueTable({
  rows,
  totalDollars,
  privacyMode = false,
}: {
  rows: SourceRevenueRow[]
  /** Everything collected in range, matched or not: the bar's 100% */
  totalDollars: number
  privacyMode?: boolean
}) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center gap-4 border-b border-ui-hairline px-[18px] py-3.5">
        <h3 className="text-sm font-medium leading-[18px] text-foreground">Where paying customers came from</h3>
      </div>
      <div className="flex items-center border-b border-ui-hairline bg-muted/60 px-[18px] py-2.5 text-xs leading-4 text-muted-foreground">
        <span className="min-w-0 flex-1 basis-0">Source</span>
        <span className={COL}>Visitors</span>
        <span className={COL}>Paid</span>
        <span className="flex w-[84px] shrink-0 justify-end text-right sm:w-[200px]">Earned</span>
      </div>
      {rows.length === 0 ? (
        <p className="px-[18px] py-8 text-center text-[13px] text-muted-foreground">
          No payment in this range matched a visit yet.
        </p>
      ) : (
        rows.map((row) => {
          const share = totalDollars > 0 ? Math.min(1, row.earnedDollars / totalDollars) : 0
          return (
            <div key={row.key} className="flex items-center border-b border-ui-rowline px-[18px] py-3.5 last:border-b-0">
              <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5">
                <span className="truncate text-sm font-medium leading-[18px] text-foreground">{row.name}</span>
                {row.description && (
                  <span className="truncate text-xs leading-4 text-ui-secondary">{row.description}</span>
                )}
              </div>
              <span className={`${COL} text-sm leading-[18px] text-foreground tabular-nums`}>
                {row.visitors == null ? '—' : fmtCount(row.visitors)}
              </span>
              <span className={`${COL} text-sm leading-[18px] text-foreground tabular-nums`}>
                {privatize(fmtCount(row.payments), privacyMode)}
              </span>
              <span className="flex w-[84px] shrink-0 items-center justify-end gap-3 sm:w-[200px]">
                <span className="relative hidden h-1 w-[120px] rounded-[2px] bg-border sm:block">
                  <span
                    className="absolute inset-y-0 left-0 rounded-[2px] bg-primary-tint-ring"
                    style={{ width: `${share * 100}%` }}
                  />
                </span>
                <span className="min-w-[56px] text-right text-sm font-medium leading-[18px] text-ui-title tabular-nums">
                  {privatize(fmtMoney(row.earnedDollars), privacyMode)}
                </span>
              </span>
            </div>
          )
        })
      )}
    </Card>
  )
}
